import { useCreateLotteryMutation } from '@lotta/self-service/lottery-fe/data-access-state';
import type { LotteryConfig, TranslationConfig } from '@lotta/shared/types-lottery-schemas';
import { useCallback, useEffect } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { LotteryForm } from './LotteryForm';
import { LotteryFormContext } from './lottery-form-context';
import type { ReviewErrorTranslationKeys } from './review-configuration/ReviewConfiguration';
import { getTemplate } from './templates';
import { buildApiError } from './utils/error';
import type { OccurrenceModule } from './select-occurrence/occurrences';
import type { WinnerCommunicationModule } from './select-winner-communication/winner-communications';

const reviewTranslationKeys: ReviewErrorTranslationKeys = {
  // eslint-disable-next-line i18next/no-literal-string
  titleTranslationKey: 'general-error',
  // eslint-disable-next-line i18next/no-literal-string
  bodyTranslationKey: 'create-contest-inline-message-error',
};

export type CreateLotteryFormProps = {
  templateId: string;
  occurrenceModule: OccurrenceModule;
  winnerCommunicationModule: WinnerCommunicationModule;
  retailUnit: string;
  onLotteryCreated: (lotteryId: string) => void;
  onCancelled: () => void;
};

export const CreateLotteryForm: React.FC<CreateLotteryFormProps> = ({
  templateId,
  occurrenceModule,
  winnerCommunicationModule,
  retailUnit,
  onLotteryCreated,
  onCancelled,
}) => {
  const [createLottery, createLotteryResult] = useCreateLotteryMutation();
  const { isLoading: isCreateLotteryLoading } = createLotteryResult;

  const onFormSubmit: SubmitHandler<{ lottery: LotteryConfig; translation: TranslationConfig }> =
    useCallback(
      (lotteryConfigData) => createLottery({ retailUnit, ...lotteryConfigData }),
      [createLottery, retailUnit]
    );

  const resetAPIError = useCallback(() => {
    createLotteryResult.reset();
  }, [createLotteryResult]);

  useEffect(() => {
    if (createLotteryResult.isSuccess) {
      onLotteryCreated(createLotteryResult.data.id);
    }
  }, [createLotteryResult, onLotteryCreated]);

  const template = getTemplate(templateId);
  template.general.push(occurrenceModule);
  template.winnerCommunication.push(winnerCommunicationModule);

  return (
    <LotteryFormContext.Provider
      value={{ mode: 'OPEN', template, result: { isCreateLotteryLoading } }}
    >
      <LotteryForm
        templateId={templateId}
        template={template}
        retailUnit={retailUnit}
        onFormSubmit={onFormSubmit}
        onSecondaryAction={onCancelled}
        error={
          createLotteryResult.isError
            ? buildApiError(createLotteryResult.isError, reviewTranslationKeys, resetAPIError)
            : undefined
        }
      />
    </LotteryFormContext.Provider>
  );
};
